<script lang="ts">
  import { currentRoute, navigate } from '../lib/router'
  import { clearToken, clearUserType, clearUserName, getUserType, getUserName, getToken, setUserName, api } from '../lib/api'
  import { fade, slide } from 'svelte/transition'
  import { onMount } from 'svelte'
  import { addToast } from '../lib/toast'

  let route = '/'
  let isMobileMenuOpen = false
  let userType: string | null = null
  let userName: string | null = null
  
  // Modal de cambio de contraseña
  let showPasswordModal = false
  let passwordForm = {
    newPassword: '',
    confirmPassword: ''
  }
  let passwordLoading = false
  
  // Modal de API Token
  let showAPITokenModal = false
  let apiTokenLoading = false
  let apiTokenData: { 
    token?: string; 
    user_id?: string; 
    name?: string;
    id?: string;
    exists?: boolean;
    created_at?: string;
  } | null = null
  let currentUserId: string | null = null
  
  currentRoute.subscribe(value => {
    route = value
    // Actualizar userType cuando cambie la ruta para reflejar cambios después del login
    userType = getUserType()
    userName = getUserName()
  })

  // Hacer userType y userName reactivos para que se actualicen cuando cambie la ruta
  // Esto asegura que se actualicen después del login o cuando cambie la navegación
  $: if (route) {
    userType = getUserType()
    userName = getUserName()
  }
  
  onMount(async () => {
    // Asegurar que userType y userName se inicialicen correctamente al montar
    userType = getUserType()
    userName = getUserName()
    
    // Si no hay nombre pero hay token, obtener la información del usuario
    if (getToken()) {
      try {
        const userInfo = await api.get('/api/v1/auth/me')
        if (userInfo.id) {
          currentUserId = userInfo.id
        }
        if (!userName) {
          if (userInfo.name) {
            userName = userInfo.name
            setUserName(userInfo.name)
          } else if (userInfo.email) {
            userName = userInfo.email
            setUserName(userInfo.email)
          }
        }
      } catch (e) {
        console.error('Error obteniendo información del usuario:', e)
      }
    }
  })
  
  // Función para traducir el tipo de usuario
  function getUserTypeLabel(type: string | null): string {
    if (!type) return ''
    switch (type) {
      case 'admin':
        return 'Administrador'
      case 'client':
        return 'Cliente'
      case 'external_app':
        return 'Aplicación Externa'
      default:
        return type
    }
  }

  function logout() {
    clearToken()
    clearUserType()
    clearUserName()
    navigate('/login')
  }

  function handleNav(path: string) {
    navigate(path)
  }

  function toggleMobileMenu() {
    isMobileMenuOpen = !isMobileMenuOpen
  }

  function closeMobileMenu() {
    isMobileMenuOpen = false
  }
  
  async function handleChangePassword() {
    if (!passwordForm.newPassword || !passwordForm.confirmPassword) {
      addToast('Por favor completa todos los campos', 'error')
      return
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      addToast('Las contraseñas no coinciden', 'error')
      return
    }
    
    if (passwordForm.newPassword.length < 6) {
      addToast('La contraseña debe tener al menos 6 caracteres', 'error')
      return
    }
    
    passwordLoading = true
    try {
      await api.put('/api/v1/auth/password', {
        new_password: passwordForm.newPassword
      })
      addToast('Contraseña actualizada exitosamente', 'success')
      showPasswordModal = false
      passwordForm = { newPassword: '', confirmPassword: '' }
    } catch (e: any) {
      addToast('Error al cambiar contraseña: ' + e.message, 'error')
    } finally {
      passwordLoading = false
    }
  }

  async function openAPITokenModal() {
    apiTokenLoading = true
    showAPITokenModal = true
    try {
      // Primero intentar obtener el token actual
      const res = await api.get(`/api/v1/auth/api-token`)
      apiTokenData = res
      if (!res.exists || !res.token) {
        // Si no existe, crear uno nuevo
        await createNewAPIToken()
      }
    } catch (e: any) {
      // Si el endpoint no existe o hay error, intentar crear uno nuevo
      if (e.message.includes('404') || e.message.includes('not found')) {
        await createNewAPIToken()
      } else {
        addToast('Error al obtener token API: ' + e.message, 'error')
        showAPITokenModal = false
      }
    } finally {
      apiTokenLoading = false
    }
  }

  async function createNewAPIToken() {
    apiTokenLoading = true
    try {
      const res = await api.post(`/api/v1/auth/api-token`)
      apiTokenData = { ...res, exists: false }
      addToast('Token API creado exitosamente', 'success')
    } catch (e: any) {
      addToast('Error al crear token API: ' + e.message, 'error')
      showAPITokenModal = false
    } finally {
      apiTokenLoading = false
    }
  }

  async function regenerateAPIToken() {
    apiTokenLoading = true
    try {
      const res = await api.post(`/api/v1/auth/api-token`)
      apiTokenData = { ...res, exists: false }
      addToast('Token API regenerado exitosamente', 'success')
    } catch (e: any) {
      addToast('Error al regenerar token API: ' + e.message, 'error')
    } finally {
      apiTokenLoading = false
    }
  }

  function copyAPIToken() {
    if (apiTokenData?.token) {
      navigator.clipboard.writeText(apiTokenData.token)
      addToast('Token copiado al portapapeles', 'success')
    }
  }

  // Links base - siempre visibles
  const baseLinks = [
    { to: '/sessions', label: 'Sesiones', icon: '📱' },
    { to: '/messages', label: 'Mensajes', icon: '💬' },
    { to: '/pools', label: 'Pools', icon: '📦' },
    { to: '/bot-registrations', label: 'Códigos Bot', icon: '🤖' },
  ]

  // Links de admin - solo para usuarios admin
  const adminLinks = [
    { to: '/admin/sessions', label: 'Admin Sesiones', icon: '🛡️' },
    { to: '/admin/users/clients', label: 'Usuarios Whatsapp', icon: '👥' },
    { to: '/admin/users/external-apps', label: 'Usuarios WebSocket', icon: '🔌' },
    { to: '/admin/config', label: 'Configuración', icon: '⚙️' },
  ]

  // Combinar links según el tipo de usuario
  $: links = userType === 'admin' 
    ? [...baseLinks, ...adminLinks]
    : baseLinks
</script>

<div class="min-h-screen relative bg-gray-900">
  <!-- Animated Background Gradients (Shared) -->
  <div class="fixed inset-0 w-full h-full z-0 pointer-events-none">
    <div class="absolute top-0 -left-4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
    <div class="absolute top-0 -right-4 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
    <div class="absolute -bottom-8 left-20 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
    <div class="absolute inset-0 bg-gradient-to-br from-indigo-900/40 via-purple-900/40 to-slate-900/80 backdrop-blur-3xl"></div>
  </div>

  <!-- Mobile Header -->
  <div class="lg:hidden fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-black/20 backdrop-blur-xl border-b border-white/5 flex justify-between items-center">
    <h1 class="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-300 tracking-tight">
      Monitor
    </h1>
    <button on:click={toggleMobileMenu} class="text-white p-2">
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {#if isMobileMenuOpen}
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        {:else}
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        {/if}
      </svg>
    </button>
  </div>

  <!-- Mobile Drawer Overlay -->
  {#if isMobileMenuOpen}
    <div 
      transition:fade 
      class="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm lg:hidden"
      on:click={closeMobileMenu}
    ></div>
    
    <aside 
      transition:slide={{ axis: 'x', duration: 300 }}
      class="fixed inset-y-0 left-0 z-50 w-64 bg-[#1a1b26] border-r border-white/10 shadow-2xl flex flex-col lg:hidden"
    >
      <div class="p-6 border-b border-white/5">
        <h1 class="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-300 tracking-tight">
          Monitor
        </h1>
        <p class="text-indigo-200/40 text-xs font-medium tracking-wider mt-1">PANEL DE CONTROL</p>
      </div>

      <!-- User Info Mobile -->
      {#if userName || userType}
        <div class="px-6 py-4 border-b border-white/5">
          <div class="flex flex-col gap-1">
            {#if userName}
              <p class="text-white font-semibold text-sm truncate" title={userName}>
                {userName}
              </p>
            {/if}
            {#if userType}
              <p class="text-indigo-300/70 text-xs font-medium">
                {getUserTypeLabel(userType)}
              </p>
            {/if}
          </div>
        </div>
      {/if}

      <nav class="flex-1 p-4 space-y-2 overflow-y-auto">
        {#each links as link}
          <button 
            on:click={() => { handleNav(link.to); closeMobileMenu(); }}
            class="group w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 {route.startsWith(link.to) ? 'bg-white/10 text-white shadow-lg shadow-purple-500/10 border border-white/10' : 'text-indigo-200/60 hover:bg-white/5 hover:text-white'}"
          >
            <span class="text-xl group-hover:scale-110 transition-transform duration-300">{link.icon}</span>
            <span class="font-bold tracking-wide text-sm">{link.label}</span>
            {#if route.startsWith(link.to)}
              <div class="ml-auto w-1.5 h-1.5 rounded-full bg-pink-400 shadow-[0_0_10px_rgba(244,114,182,0.8)]"></div>
            {/if}
          </button>
        {/each}
      </nav>

      <div class="p-4 border-t border-white/5 bg-black/20 space-y-2">
        <button 
          on:click={() => { showPasswordModal = true; closeMobileMenu(); }}
          class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-indigo-200/60 hover:bg-blue-500/10 hover:text-blue-300 transition-all duration-300 group"
        >
          <span class="text-xl group-hover:rotate-12 transition-transform">🔒</span>
          <span class="font-medium text-sm">Cambiar Contraseña</span>
        </button>
        <button 
          on:click={() => { openAPITokenModal(); closeMobileMenu(); }}
          disabled={apiTokenLoading}
          class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-indigo-200/60 hover:bg-purple-500/10 hover:text-purple-300 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span class="text-xl group-hover:scale-110 transition-transform">🔑</span>
          <span class="font-medium text-sm">{apiTokenLoading ? 'Cargando...' : 'API Token'}</span>
        </button>
        <button 
          on:click={logout}
          class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-indigo-200/60 hover:bg-red-500/10 hover:text-red-300 transition-all duration-300 group"
        >
          <span class="text-xl group-hover:-translate-x-1 transition-transform">🚪</span>
          <span class="font-medium text-sm">Cerrar Sesión</span>
        </button>
      </div>
    </aside>
  {/if}

  <!-- Desktop Sidebar -->
  <aside class="hidden lg:flex fixed left-0 top-0 h-screen w-72 z-10 bg-black/20 backdrop-blur-xl border-r border-white/5 flex-col shadow-2xl">
    <div class="p-8 border-b border-white/5 flex-shrink-0">
      <h1 class="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-300 tracking-tight">
        Monitor
      </h1>
      <p class="text-indigo-200/40 text-xs font-medium tracking-wider mt-1">PANEL DE CONTROL</p>
    </div>

    <!-- User Info -->
    {#if userName || userType}
      <div class="px-6 py-4 border-b border-white/5 flex-shrink-0">
        <div class="flex flex-col gap-1">
          {#if userName}
            <p class="text-white font-semibold text-sm truncate" title={userName}>
              {userName}
            </p>
          {/if}
          {#if userType}
            <p class="text-indigo-300/70 text-xs font-medium">
              {getUserTypeLabel(userType)}
            </p>
          {/if}
        </div>
      </div>
    {/if}

    <nav class="flex-1 p-4 space-y-2 overflow-y-auto">
      {#each links as link}
        <button 
          on:click={() => handleNav(link.to)}
          class="group w-full flex items-center gap-4 px-4 py-4 rounded-xl transition-all duration-300 {route.startsWith(link.to) ? 'bg-white/10 text-white shadow-lg shadow-purple-500/10 border border-white/10' : 'text-indigo-200/60 hover:bg-white/5 hover:text-white hover:pl-6'}"
        >
          <span class="text-2xl group-hover:scale-110 transition-transform duration-300">{link.icon}</span>
          <span class="font-bold tracking-wide">{link.label}</span>
          {#if route.startsWith(link.to)}
            <div class="ml-auto w-1.5 h-1.5 rounded-full bg-pink-400 shadow-[0_0_10px_rgba(244,114,182,0.8)]"></div>
          {/if}
        </button>
      {/each}
    </nav>

    <div class="p-4 border-t border-white/5 bg-black/20 flex-shrink-0 space-y-2">
      <button 
        on:click={() => showPasswordModal = true}
        class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-indigo-200/60 hover:bg-blue-500/10 hover:text-blue-300 transition-all duration-300 group"
      >
        <span class="text-xl group-hover:rotate-12 transition-transform">🔒</span>
        <span class="font-medium">Cambiar Contraseña</span>
      </button>
      <button 
        on:click={openAPITokenModal}
        disabled={apiTokenLoading}
        class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-indigo-200/60 hover:bg-purple-500/10 hover:text-purple-300 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span class="text-xl group-hover:scale-110 transition-transform">🔑</span>
        <span class="font-medium">{apiTokenLoading ? 'Cargando...' : 'API Token'}</span>
      </button>
      <button 
        on:click={logout}
        class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-indigo-200/60 hover:bg-red-500/10 hover:text-red-300 transition-all duration-300 group"
      >
        <span class="text-xl group-hover:-translate-x-1 transition-transform">🚪</span>
        <span class="font-medium">Cerrar Sesión</span>
      </button>
    </div>
  </aside>

  <!-- Main Content -->
  <main class="relative z-10 p-4 lg:p-8 pt-20 lg:pt-8 lg:ml-72 min-h-screen">
    <div class="max-w-6xl mx-auto pb-8">
      <slot />
    </div>
  </main>
</div>

<!-- Modal de Cambio de Contraseña -->
{#if showPasswordModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" transition:fade>
    <div class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden" transition:slide>
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"></div>
      
      <h3 class="text-2xl font-black text-white mb-6">
        Cambiar Contraseña
      </h3>
      
      <div class="space-y-5">
        <div>
          <label for="newPassword" class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
            Nueva Contraseña
          </label>
          <input
            id="newPassword"
            type="password"
            bind:value={passwordForm.newPassword}
            placeholder="Ingresa tu nueva contraseña"
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all outline-none placeholder-white/20"
            disabled={passwordLoading}
          />
          <p class="mt-2 text-sm text-indigo-200/60 font-medium">
            La contraseña debe tener al menos 6 caracteres.
          </p>
        </div>
        
        <div>
          <label for="confirmPassword" class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
            Confirmar Contraseña
          </label>
          <input
            id="confirmPassword"
            type="password"
            bind:value={passwordForm.confirmPassword}
            placeholder="Confirma tu nueva contraseña"
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all outline-none placeholder-white/20"
            disabled={passwordLoading}
            on:keydown={(e) => {
              if (e.key === 'Enter' && !passwordLoading) {
                handleChangePassword()
              }
            }}
          />
        </div>
      </div>
      
      <div class="flex gap-3 mt-8">
        <button
          on:click={() => {
            showPasswordModal = false
            passwordForm = { newPassword: '', confirmPassword: '' }
          }}
          disabled={passwordLoading}
          class="flex-1 px-6 py-3 rounded-xl bg-black/30 border border-white/10 text-white hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-bold"
        >
          Cancelar
        </button>
        <button
          on:click={handleChangePassword}
          disabled={passwordLoading}
          class="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 hover:from-blue-400 hover:via-purple-400 hover:to-indigo-400 text-white font-bold shadow-lg shadow-blue-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {passwordLoading ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Modal de API Token -->
{#if showAPITokenModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" transition:fade>
    <div class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden" transition:slide>
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500"></div>
      
      <h3 class="text-2xl font-black text-white mb-6">
        {apiTokenData?.exists ? 'Token API Actual' : 'Token API Generado'}
      </h3>
      
      {#if apiTokenLoading}
        <div class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      {:else if apiTokenData?.token}
        <div class="space-y-4">
          <div>
            <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
              Token API
            </label>
            <div class="flex gap-2">
              <input
                type="text"
                value={apiTokenData.token}
                readonly
                class="flex-1 px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white font-mono text-sm"
              />
              <button
                on:click={copyAPIToken}
                class="px-4 py-3 rounded-xl bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 transition-colors"
                title="Copiar"
              >
                📋
              </button>
            </div>
            {#if apiTokenData.exists}
              <p class="mt-2 text-sm text-indigo-200/60 font-medium">
                Este es tu token API actual. Puedes regenerarlo si necesitas uno nuevo.
              </p>
            {:else}
              <p class="mt-2 text-sm text-indigo-200/60 font-medium">
                ⚠️ Guarda este token de forma segura. Solo se mostrará una vez.
              </p>
            {/if}
          </div>
          {#if apiTokenData.name}
            <div>
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
                Nombre
              </label>
              <div class="px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white text-sm">
                {apiTokenData.name}
              </div>
            </div>
          {/if}
          {#if apiTokenData.created_at}
            <div>
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
                Creado el
              </label>
              <div class="px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white text-sm">
                {new Date(apiTokenData.created_at).toLocaleString('es-ES')}
              </div>
            </div>
          {/if}
        </div>
      {:else}
        <div class="text-center py-8">
          <p class="text-indigo-200/60 mb-4">No se pudo obtener el token API.</p>
        </div>
      {/if}
      
      <div class="flex gap-3 mt-8">
        {#if apiTokenData?.exists}
          <button
            on:click={regenerateAPIToken}
            disabled={apiTokenLoading}
            class="flex-1 px-6 py-3 rounded-xl bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/30 transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {apiTokenLoading ? 'Regenerando...' : 'Regenerar Token'}
          </button>
        {/if}
        <button
          on:click={() => {
            showAPITokenModal = false
            apiTokenData = null
          }}
          class="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 hover:from-purple-400 hover:via-pink-400 hover:to-indigo-400 text-white font-bold shadow-lg shadow-purple-500/30 transition-all duration-300"
        >
          Cerrar
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob {
    animation: blob 10s infinite;
  }
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }
</style>
